/**
 * SwimAnnotationsPage.jsx — Video annotation tool for ML ground truth
 * ===================================================================
 *
 * Watch completed session videos and mark events (strokes, turns,
 * underwater phases, breakouts, etc.) frame by frame.
 * Annotations are saved to the DB and used for stroke classifier training.
 *
 * Route: /swim-vision/annotate/:sessionId
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Play, Pause, SkipBack, SkipForward, ChevronLeft, ChevronRight,
  Save, Trash2, Plus, Tag, Waves, RotateCcw, Clock, Eye, Zap,
  CheckCircle2, AlertTriangle, Download, Upload,
} from 'lucide-react';
import {
  getBiomechanicsSession, getAnnotations, saveAnnotations,
} from '../services/biomechanicsService';

/* ═══════════════════════════════════════════════════════════════════
   Design Tokens (VUKZ minimal)
   ═══════════════════════════════════════════════════════════════════ */
const T = {
  bg: '#fafafa', card: '#ffffff', text: '#111111',
  textSoft: '#6b7280', textMute: '#9ca3af', border: '#e5e7eb',
  accent: '#111111', accentSoft: '#f3f4f6',
  success: '#16a34a', successBg: '#f0fdf4',
  warning: '#f59e0b', warningBg: '#fffbeb',
  error: '#dc2626', errorBg: '#fef2f2',
  info: '#2563eb', infoBg: '#eff6ff',
  cyan: '#0891b2', cyanBg: '#ecfeff',
};

/* Event type configuration */
const EVENT_TYPES = [
  { key: 'stroke',           label: 'Braçada',         color: '#2563eb', shortcut: 'S', icon: Waves },
  { key: 'turn',             label: 'Virada',          color: '#7c3aed', shortcut: 'T', icon: RotateCcw },
  { key: 'wall_touch',       label: 'Toque na parede', color: '#ea580c', shortcut: 'W', icon: Zap },
  { key: 'underwater_start', label: 'Submerso início', color: '#0891b2', shortcut: 'U', icon: Eye },
  { key: 'underwater_end',   label: 'Submerso fim',    color: '#0d9488', shortcut: 'E', icon: Eye },
  { key: 'breakout',         label: 'Breakout',        color: '#d97706', shortcut: 'B', icon: Zap },
  { key: 'start',            label: 'Largada',         color: '#16a34a', shortcut: 'G', icon: Play },
  { key: 'finish',           label: 'Chegada',         color: '#dc2626', shortcut: 'F', icon: CheckCircle2 },
];

const EVENT_MAP = Object.fromEntries(EVENT_TYPES.map(e => [e.key, e]));

/* ═══════════════════════════════════════════════════════════════════
   Annotation Page
   ═══════════════════════════════════════════════════════════════════ */
const SwimAnnotationsPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);

  // State
  const [session, setSession] = useState(null);
  const [annotations, setAnnotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [activeType, setActiveType] = useState('stroke');
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [savedMsg, setSavedMsg] = useState('');
  const [videoError, setVideoError] = useState(null);
  const [usingFallback, setUsingFallback] = useState(false);

  // Fetch session + annotations
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [sessResp, annResp] = await Promise.all([
          getBiomechanicsSession(sessionId),
          getAnnotations(sessionId),
        ]);
        setSession(sessResp.data?.data || sessResp.data);
        setAnnotations((annResp.data?.data || []).sort((a, b) => a.time_s - b.time_s));
      } catch (err) {
        console.error('[Annotations] Load error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [sessionId]);

  const annotatedUrl = session?.annotatedVideoUrl;
  const originalUrl = session?.videoBlobUrl || session?.video_blob_url;
  const videoUrl = usingFallback ? originalUrl : (annotatedUrl || originalUrl);
  const fps = session?.fps || 30;

  // Add annotation at current time
  const addAnnotation = useCallback(() => {
    if (!videoRef.current) return;
    const t = videoRef.current.currentTime;
    const frameNum = Math.round(t * fps);
    const newAnn = {
      event_type: activeType,
      time_s: Math.round(t * 1000) / 1000,
      frame_number: frameNum,
      confidence: 1.0,
      source: 'manual',
      notes: '',
    };
    setAnnotations(prev => [...prev, newAnn].sort((a, b) => a.time_s - b.time_s));
    setDirty(true);
  }, [activeType, fps]);

  // Delete annotation
  const removeAnnotation = (idx) => {
    setAnnotations(prev => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };

  // Save to backend
  const handleSave = async () => {
    setSaving(true);
    try {
      await saveAnnotations(sessionId, annotations);
      setDirty(false);
      setSavedMsg('✓ Salvo');
      setTimeout(() => setSavedMsg(''), 2000);
    } catch (err) {
      console.error('[Annotations] Save error:', err);
      alert(`Erro ao salvar: ${err.response?.data?.error || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Video controls
  const RATES = [0.1, 0.25, 0.5, 0.75, 1, 1.5, 2];

  const togglePlay = useCallback(() => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) videoRef.current.play();
    else videoRef.current.pause();
  }, []);

  const seekFrame = useCallback((delta) => {
    if (!videoRef.current) return;
    videoRef.current.currentTime += delta / fps;
  }, [fps]);

  const seekTo = useCallback((t) => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = t;
  }, []);

  const changeRate = useCallback(() => {
    setPlaybackRate(prev => {
      const next = RATES[(RATES.indexOf(prev) + 1) % RATES.length];
      if (videoRef.current) videoRef.current.playbackRate = next;
      return next;
    });
  }, []);

  const speedDown = useCallback(() => {
    setPlaybackRate(prev => {
      const idx = RATES.indexOf(prev);
      const next = RATES[Math.max(0, idx - 1)];
      if (videoRef.current) videoRef.current.playbackRate = next;
      return next;
    });
  }, []);

  const speedUp = useCallback(() => {
    setPlaybackRate(prev => {
      const idx = RATES.indexOf(prev);
      const next = RATES[Math.min(RATES.length - 1, idx + 1)];
      if (videoRef.current) videoRef.current.playbackRate = next;
      return next;
    });
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      // Event type shortcuts
      const type = EVENT_TYPES.find(t => t.shortcut.toLowerCase() === e.key.toLowerCase());
      if (type) {
        setActiveType(type.key);
        return;
      }

      switch (e.key) {
        case ' ':
          e.preventDefault();
          togglePlay();
          break;
        case 'Enter':
          e.preventDefault();
          addAnnotation();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          seekFrame(e.shiftKey ? -10 : -1);
          break;
        case 'ArrowRight':
          e.preventDefault();
          seekFrame(e.shiftKey ? 10 : 1);
          break;
        case '[':
          e.preventDefault();
          speedDown();
          break;
        case ']':
          e.preventDefault();
          speedUp();
          break;
        case 'z':
        case 'Z':
          if (e.metaKey || e.ctrlKey) {
            e.preventDefault();
            setAnnotations(prev => prev.slice(0, -1));
            setDirty(true);
          }
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [togglePlay, addAnnotation, seekFrame, speedDown, speedUp]);

  // Export annotations as JSON
  const exportJSON = () => {
    const blob = new Blob([JSON.stringify({
      session_id: parseInt(sessionId),
      style: session?.swimming_style,
      distance_m: session?.distance_m,
      fps,
      annotations,
    }, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `annotations_session_${sessionId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Import annotations from JSON
  const importJSON = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const data = JSON.parse(ev.target.result);
          const imported = data.annotations || data;
          if (Array.isArray(imported)) {
            setAnnotations(prev => [...prev, ...imported].sort((a, b) => a.time_s - b.time_s));
            setDirty(true);
          }
        } catch (err) {
          alert('Erro ao importar JSON');
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  // Count by type
  const counts = annotations.reduce((acc, a) => {
    acc[a.event_type] = (acc[a.event_type] || 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: T.bg }}>
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-gray-300 border-t-black rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm" style={{ color: T.textSoft }}>Carregando sessão #{sessionId}...</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: T.bg }}>
        <div className="text-center">
          <AlertTriangle size={32} className="mx-auto mb-3" style={{ color: T.warning }} />
          <p className="font-bold" style={{ color: T.text }}>Sessão #{sessionId} não encontrada</p>
          <Link to="/swim-vision/dashboard" className="text-sm underline" style={{ color: T.info }}>
            Voltar ao Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const fmtTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = (s % 60).toFixed(2);
    return `${m}:${sec.padStart(5, '0')}`;
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: T.bg }}>
      {/* Header */}
      <div className="sticky top-0 z-40 border-b" style={{ backgroundColor: T.card, borderColor: T.border }}>
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-gray-100">
            <ArrowLeft size={18} />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold" style={{ color: T.text }}>
              Anotações — Sessão #{sessionId}
            </h1>
            <p className="text-xs" style={{ color: T.textSoft }}>
              {session.swimming_style} · {session.distance_m}m · {session.video_filename || 'vídeo'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {savedMsg && (
              <span className="text-xs font-bold px-2 py-1 rounded" style={{ color: T.success, backgroundColor: T.successBg }}>
                {savedMsg}
              </span>
            )}
            {dirty && (
              <span className="text-xs px-2 py-1 rounded" style={{ color: T.warning, backgroundColor: T.warningBg }}>
                Não salvo
              </span>
            )}
            <button onClick={importJSON}
              className="p-2 rounded-lg hover:bg-gray-100" title="Importar JSON">
              <Upload size={16} style={{ color: T.textSoft }} />
            </button>
            <button onClick={exportJSON}
              className="p-2 rounded-lg hover:bg-gray-100" title="Exportar JSON">
              <Download size={16} style={{ color: T.textSoft }} />
            </button>
            <button onClick={handleSave} disabled={saving || !dirty}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold text-white disabled:opacity-40"
              style={{ backgroundColor: T.accent }}>
              <Save size={14} />
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: Video + Controls */}
        <div className="lg:col-span-2 space-y-3">
          {/* Video */}
          <div className="rounded-xl overflow-hidden bg-black relative" style={{ aspectRatio: '16/9' }}>
            {videoUrl ? (
              <>
                <video
                  ref={videoRef}
                  src={videoUrl}
                  className="w-full h-full object-contain"
                  onTimeUpdate={() => setCurrentTime(videoRef.current?.currentTime || 0)}
                  onLoadedMetadata={() => {
                    setDuration(videoRef.current?.duration || 0);
                    videoRef.current.playbackRate = playbackRate;
                  }}
                  onPlay={() => setPlaying(true)}
                  onPause={() => setPlaying(false)}
                  onError={(e) => {
                    const msg = e.target.error?.message || 'Falha ao carregar';
                    console.error('[Video] Load error:', msg, 'src:', videoUrl?.substring(0, 100));
                    if (!usingFallback && originalUrl && videoUrl !== originalUrl) {
                      console.warn('[Video] Annotated failed, falling back to original');
                      setUsingFallback(true);
                      setVideoError(null);
                    } else {
                      setVideoError(msg);
                    }
                  }}
                />
                {videoError && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/80">
                    <div className="text-center p-4">
                      <p className="text-red-400 text-sm font-bold">❌ Erro no vídeo</p>
                      <p className="text-white/60 text-xs mt-1">{videoError}</p>
                      <p className="text-white/40 text-xs mt-2 break-all max-w-md">{videoUrl?.substring(0, 120)}...</p>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <p className="text-white/50 text-sm">Vídeo não disponível (sem URL)</p>
              </div>
            )}
          </div>

          {/* Timeline */}
          <div className="rounded-xl border p-3" style={{ backgroundColor: T.card, borderColor: T.border }}>
            {/* Progress bar with markers */}
            <div className="relative h-8 bg-gray-100 rounded-lg mb-2 cursor-pointer"
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const pct = (e.clientX - rect.left) / rect.width;
                seekTo(pct * duration);
              }}>
              {/* Playhead */}
              <div className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10"
                style={{ left: `${(currentTime / Math.max(duration, 1)) * 100}%` }} />
              {/* Annotation markers */}
              {annotations.map((ann, i) => {
                const cfg = EVENT_MAP[ann.event_type] || { color: '#999' };
                return (
                  <div key={i} className="absolute top-1 w-1.5 h-6 rounded-full opacity-80 hover:opacity-100 cursor-pointer"
                    style={{
                      left: `${(ann.time_s / Math.max(duration, 1)) * 100}%`,
                      backgroundColor: cfg.color,
                    }}
                    title={`${cfg.label || ann.event_type} @ ${fmtTime(ann.time_s)}`}
                    onClick={(e) => { e.stopPropagation(); seekTo(ann.time_s); }}
                  />
                );
              })}
            </div>

            {/* Controls row */}
            <div className="flex items-center gap-2">
              <button onClick={() => seekFrame(-10)} className="p-1.5 rounded hover:bg-gray-100" title="← 10 frames">
                <SkipBack size={14} />
              </button>
              <button onClick={() => seekFrame(-1)} className="p-1.5 rounded hover:bg-gray-100" title="← 1 frame">
                <ChevronLeft size={14} />
              </button>
              <button onClick={togglePlay}
                className="p-2 rounded-lg text-white" style={{ backgroundColor: T.accent }}>
                {playing ? <Pause size={16} /> : <Play size={16} />}
              </button>
              <button onClick={() => seekFrame(1)} className="p-1.5 rounded hover:bg-gray-100" title="→ 1 frame">
                <ChevronRight size={14} />
              </button>
              <button onClick={() => seekFrame(10)} className="p-1.5 rounded hover:bg-gray-100" title="→ 10 frames">
                <SkipForward size={14} />
              </button>

              <div className="flex-1 text-center">
                <span className="text-xs font-mono font-bold" style={{ color: T.text }}>
                  {fmtTime(currentTime)} / {fmtTime(duration)}
                </span>
                <span className="text-xs ml-2" style={{ color: T.textMute }}>
                  f{Math.round(currentTime * fps)}
                </span>
              </div>

              <div className="flex items-center gap-0.5">
                <button onClick={speedDown} title="Slower [" 
                  className="px-1.5 py-1 rounded-l text-xs font-bold border"
                  style={{ borderColor: T.border, color: T.text }}>−</button>
                <button onClick={changeRate}
                  className="px-2 py-1 text-xs font-bold border-y"
                  style={{ borderColor: T.border, color: playbackRate < 1 ? '#ef4444' : T.text, backgroundColor: playbackRate < 1 ? '#fef2f2' : 'transparent' }}>
                  {playbackRate}×
                </button>
                <button onClick={speedUp} title="Faster ]"
                  className="px-1.5 py-1 rounded-r text-xs font-bold border"
                  style={{ borderColor: T.border, color: T.text }}>+</button>
              </div>

              {/* Add annotation button */}
              <button onClick={addAnnotation}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-bold text-white"
                style={{ backgroundColor: EVENT_MAP[activeType]?.color || T.accent }}>
                <Plus size={14} />
                {EVENT_MAP[activeType]?.label || activeType}
              </button>
            </div>
          </div>

          {/* Event type selector */}
          <div className="rounded-xl border p-3" style={{ backgroundColor: T.card, borderColor: T.border }}>
            <p className="text-xs font-bold mb-2" style={{ color: T.textSoft }}>
              Tipo de evento (atalhos: {EVENT_TYPES.map(e => e.shortcut).join(', ')}) · Enter = adicionar · Espaço = play/pause · ←→ = frame
            </p>
            <div className="flex flex-wrap gap-2">
              {EVENT_TYPES.map(evt => (
                <button key={evt.key}
                  onClick={() => setActiveType(evt.key)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border-2 transition-all"
                  style={{
                    borderColor: activeType === evt.key ? evt.color : T.border,
                    backgroundColor: activeType === evt.key ? evt.color + '15' : T.card,
                    color: activeType === evt.key ? evt.color : T.textSoft,
                  }}>
                  <evt.icon size={12} />
                  {evt.label}
                  <span className="text-[10px] opacity-60">({evt.shortcut})</span>
                  {counts[evt.key] ? (
                    <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] text-white"
                      style={{ backgroundColor: evt.color }}>
                      {counts[evt.key]}
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Annotations list */}
        <div className="space-y-3">
          {/* Summary */}
          <div className="rounded-xl border p-3" style={{ backgroundColor: T.card, borderColor: T.border }}>
            <h3 className="text-sm font-bold mb-2" style={{ color: T.text }}>
              Resumo ({annotations.length} anotações)
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {EVENT_TYPES.filter(e => counts[e.key]).map(evt => (
                <div key={evt.key} className="flex items-center gap-2 text-xs">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: evt.color }} />
                  <span style={{ color: T.textSoft }}>{evt.label}:</span>
                  <span className="font-bold" style={{ color: T.text }}>{counts[evt.key]}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Annotations list */}
          <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: T.card, borderColor: T.border }}>
            <div className="px-3 py-2 border-b flex items-center justify-between" style={{ borderColor: T.border }}>
              <h3 className="text-sm font-bold" style={{ color: T.text }}>Eventos marcados</h3>
              <span className="text-xs" style={{ color: T.textMute }}>{annotations.length}</span>
            </div>
            <div className="max-h-[60vh] overflow-y-auto divide-y" style={{ divideColor: T.border }}>
              {annotations.length === 0 && (
                <div className="p-6 text-center">
                  <Tag size={20} className="mx-auto mb-2" style={{ color: T.textMute }} />
                  <p className="text-xs" style={{ color: T.textSoft }}>
                    Nenhuma anotação ainda.<br />
                    Use os atalhos ou o botão + para marcar eventos.
                  </p>
                </div>
              )}
              {annotations.map((ann, idx) => {
                const cfg = EVENT_MAP[ann.event_type] || { label: ann.event_type, color: '#999' };
                const isNear = Math.abs(ann.time_s - currentTime) < 0.5;
                return (
                  <div key={idx}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer transition-colors"
                    style={{ backgroundColor: isNear ? cfg.color + '08' : undefined }}
                    onClick={() => seekTo(ann.time_s)}>
                    <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: cfg.color }} />
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-bold" style={{ color: cfg.color }}>{cfg.label}</span>
                      <span className="text-[10px] ml-1.5 font-mono" style={{ color: T.textMute }}>
                        {fmtTime(ann.time_s)} · f{ann.frame_number || Math.round(ann.time_s * fps)}
                      </span>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); removeAnnotation(idx); }}
                      className="p-1 rounded hover:bg-red-50 shrink-0">
                      <Trash2 size={12} style={{ color: T.error }} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SwimAnnotationsPage;
